import { ShareDialogAccessLevel } from "@components/access-level/share-dialog-access-level";
import { CopyLinkButton } from "@components/common/button/copy-link-button";
import { SPACE_ELEMENTS_OF_MODAL } from "@components/common/dialog/faro-dialog";
import { SphereActionDivider } from "@components/common/sphere-action-divider";
import { SphereDivider } from "@components/common/sphere-divider";
import { BaseProjectProps } from "@custom-types/project-types";
import { Dialog } from "@flat-ui";
import { Skeleton } from "@mui/material";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { defaultProjectLaunchTargetSelector } from "@store/sdb-company/sdb-company-selector";
import {
  isSelectedProjectEditableSelector,
  selectedProjectSelector,
} from "@store/projects/projects-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { getOpenProjectHref } from "@utils/project-utils";
import { ProjectEvents } from "@utils/track-event/track-event-list";
import { PropsWithChildren, useEffect, useState } from "react";
import { InviteMemberToProjectDialog } from "@pages/projects/invite-member-to-project-dialog";
import { ProjectMembersAccordion } from "@pages/projects/project-members-accordion";
import { fetchProjectDetails } from "@store/projects/projects-slice-thunk";
import { useCompanyId } from "@context-providers/company-id-provider";
import { useErrorContext } from "@context-providers/error-boundary/error-handling-context";

interface Props extends BaseProjectProps {
  /** Determines if the dialog should open */
  isOpen: boolean;

  /** Function to call when the dialog should close */
  onClose: () => void;
}

/** Reusable share dialog for a project which includes access level management and member invitations */
export function ProjectShareDialog({
  project,
  isOpen,
  onClose,
  children: triggerButton,
}: PropsWithChildren<Props>): JSX.Element {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const { handleErrorWithToast } = useErrorContext();
  const { defaultOpenProjectTarget } = useAppSelector(
    defaultProjectLaunchTargetSelector
  );
  const selectedProjectInStore = useAppSelector(selectedProjectSelector);

  // Stream endpoints (e.g. the projects map view) return projects without the
  // "permissions" property, so permission checks against the passed project would
  // always fail. When the fully-loaded project is available in the store we use it
  // instead, so the access level and member sections are evaluated correctly.
  const resolvedProject =
    selectedProjectInStore?.id === project.id
      ? selectedProjectInStore
      : project;

  const isProjectEditable = useAppSelector(isSelectedProjectEditableSelector());
  const {
    canInviteUsersToPrivateProject,
    canViewAllProjectMembers,
    canChangeProjectAccessLevel,
  } = useHasUserValidPermissionProjectLevel({ project: resolvedProject });

  const isAccessLevelEditable =
    isProjectEditable && canChangeProjectAccessLevel;

  // Whether the project details still need to be fetched to evaluate the
  // permission-gated sections below. Used to show a skeleton placeholder so the
  // dialog layout doesn't jump when the sections appear after fetching.
  const [isLoadingDetails, setIsLoadingDetails] = useState<boolean>(
    isOpen && selectedProjectInStore?.id !== project.id
  );

  // Fetch the project details when the dialog opens for a project whose details
  // (including permissions) aren't loaded yet, so the permission-gated sections
  // below can be evaluated. Skipped when the project is already the selected one
  // to avoid an unnecessary request.
  useEffect(() => {
    if (!isOpen || selectedProjectInStore?.id === project.id) {
      setIsLoadingDetails(false);
      return;
    }

    async function fetchDetails(): Promise<void> {
      setIsLoadingDetails(true);

      try {
        await dispatch(
          fetchProjectDetails({ companyId, projectId: project.id })
        ).unwrap();
      } catch (error) {
        handleErrorWithToast({
          id: `ProjectShareDialog-fetchDetails-${Date.now().toString()}`,
          title: "Could not load project permissions",
          error,
        });
      } finally {
        setIsLoadingDetails(false);
      }
    }

    void fetchDetails();
  }, [
    isOpen,
    selectedProjectInStore?.id,
    project.id,
    companyId,
    dispatch,
    handleErrorWithToast,
  ]);

  return (
    <>
      {triggerButton}

      <Dialog
        open={isOpen}
        onClose={onClose}
        title="Share project"
        topRightActions={
          <>
            <CopyLinkButton
              targetHref={getOpenProjectHref({
                openTarget: defaultOpenProjectTarget,
                projectId: project.id,
              })}
              logEventParams={{
                name: ProjectEvents.copyShareProjectLink,
                props: {
                  projectId: project.id,
                  accessLevel: project.accessLevel,
                  location: "share-project-dialog",
                },
              }}
            />
            <SphereActionDivider />
          </>
        }
      >
        <ShareDialogAccessLevel
          type="project"
          entity={resolvedProject}
          isAccessLevelEditable={isAccessLevelEditable}
          location="project-share-dialog"
        />

        {isLoadingDetails ? (
          <>
            <SphereDivider sx={{ my: SPACE_ELEMENTS_OF_MODAL }} />
            <Skeleton variant="rectangular" height="47px" />
            <SphereDivider sx={{ mt: SPACE_ELEMENTS_OF_MODAL, mb: "12px" }} />
            <Skeleton variant="rectangular" height="48px" />
          </>
        ) : (
          <>
            {canInviteUsersToPrivateProject && (
              <>
                <SphereDivider sx={{ my: SPACE_ELEMENTS_OF_MODAL }} />
                <InviteMemberToProjectDialog project={resolvedProject} />
              </>
            )}

            {canViewAllProjectMembers && (
              <>
                <SphereDivider
                  sx={{ mt: SPACE_ELEMENTS_OF_MODAL, mb: "12px" }}
                />
                <ProjectMembersAccordion
                  project={resolvedProject}
                  location="share-project-dialog"
                />
              </>
            )}
          </>
        )}
      </Dialog>
    </>
  );
}
